// libs
import { makeStyles } from '@material-ui/styles'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'

// Components
import DocumentMeta from 'core/components/DocumentMeta'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import Progress from 'core/components/progress/Progress'
import Button from 'core/elements/button/Button'
import { HeaderRefreshPortal } from 'core/elements/header/portals'
import useListAction from 'core/hooks/useListAction'
import { listClusterHostGroups } from '../actions'
import { clusterHostGroupsSelector } from '../selectors'
import ClusterHostGroupOverview from './ClusterHostGroupOverview'
import StatusCard from 'core/components/statusCard/StatusCard'
import StatusIconText from 'k8s/components/dashboard/StatusIconText'
import { IClusterHostGroup } from '../model'
import { generateHostStatus } from '../helpers'
import HostsListPage from '../../hosts/HostsListPage'
import { listVmHaClusters } from '../../actions'

const ClusterDetails = () => {
  const { match, history } = useReactRouter()
  const { id } = match.params

  // Actions
  const { loading, reload } = useListAction(listClusterHostGroups)
  const { loading: vmhaLoading, reload: vmhaReload } = useListAction(listVmHaClusters)
  const clusterHostGroups = useSelector(clusterHostGroupsSelector)
  const clusterHostGroup: IClusterHostGroup = clusterHostGroups.find(
    (clusterHostGroup) => clusterHostGroup.name === id,
  )
  const clusterHostsStatus = useMemo(() => {
    return generateHostStatus(clusterHostGroup?.hosts)
  }, [clusterHostGroup])

  const classes = useStyles({ containsHosts: clusterHostGroup?.hosts?.length > 0 })

  return (
    <>
      <HeaderRefreshPortal>
        <Button
          icon="refresh"
          variant="secondary"
          onClick={() => {
            reload(true)
            vmhaReload(true)
          }}
        >
          Refresh
        </Button>
      </HeaderRefreshPortal>
      <Progress loading={loading}>
        <DocumentMeta title="Dashboard" />
        {clusterHostGroup ? (
          <>
            <div className={classes.customGridContainer}>
              <div className={classes.customGridItem}>
                <StatusCard show={true} title={`Hosts (${clusterHostGroup.hosts.length})`}>
                  {clusterHostsStatus?.map((item, index) => (
                    <StatusIconText
                      key={index}
                      label={item?.label}
                      icon={item?.icon}
                      variant={item?.variant}
                      rightBorder={index !== clusterHostsStatus?.length - 1}
                      margin={false}
                    />
                  ))}
                </StatusCard>
                <ClusterHostGroupOverview
                  clusterHostGroup={clusterHostGroup}
                  vmhaLoading={vmhaLoading}
                />
              </div>
              <div className={classes.hostsList}>
                <HostsListPage
                  renderFromClustersPage={true}
                  hostlist={clusterHostGroup?.hostlist}
                />
              </div>
            </div>
          </>
        ) : (
          <Text variant="body1">No Clusters found</Text>
        )}
      </Progress>
    </>
  )
}

export default ClusterDetails

const useStyles = makeStyles<Theme, { containsHosts: boolean }>((theme: Theme) => ({
  customGridContainer: {
    display: 'grid',
    width: '100%',
    gridTemplateColumns: ({ containsHosts }) => (containsHosts ? '370px auto' : '370px 1fr'),
    gridGap: theme.spacing(3),
    marginRight: theme.spacing(3),
  },
  customGridItem: {
    display: 'grid',
    gridGap: theme.spacing(3),
    gridTemplateRows: '112px max-content',
  },
  headerText: {
    color: theme.components.card.passiveText,
  },
  headerSubText: {
    paddingTop: theme.spacing(1),
  },
  divider: {
    border: '0',
    height: '1px',
    margin: theme.spacing(1, 0),
    background: '#e6e6ea',
  },
  currentVersion: {
    display: 'flex',
    alignItems: 'center',
  },
  hostsList: {
    '& .progress-root': {
      height: 'auto',
    },
  },
}))
